<template>
	<button
		type="button"
		class="hold-confirm"
		:disabled="disabled"
		@pointerdown="handlePointerDown"
		@pointerup="release"
		@pointerleave="release"
		@pointercancel="release"
		@keydown="handleKeydown"
		@keyup="handleKeyup"
		@blur="release"
		@contextmenu.prevent
	>
		<span class="hold-confirm-fill" :style="{ transform: `scaleX(${progress})` }" aria-hidden="true"></span>
		<slot />
	</button>
</template>

<script setup>
import { onBeforeUnmount, ref } from 'vue';

const props = defineProps({
	// How long the button has to be held, in milliseconds.
	duration: { type: Number, default: 1000 },
	disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['confirm', 'cancel']);

// Letting go early drains the fill faster than it filled.
const DRAIN_MS = 250;
const CONFIRM_DELAY_MS = 150;

const progress = ref(0);
const holding = ref(false);
let done = false;
let frame = 0;
let lastTime = null;
let confirmTimer;

const complete = () => {
	holding.value = false;
	done = true;
	// The full fill stays for a moment before the item goes.
	confirmTimer = setTimeout(() => emit('confirm'), CONFIRM_DELAY_MS);
};

const tick = (now) => {
	const elapsed = lastTime === null ? 0 : Math.max(0, now - lastTime);
	lastTime = now;
	frame = 0;
	if (holding.value) {
		progress.value = Math.min(1, progress.value + elapsed / props.duration);
		if (progress.value >= 1) {
			complete();
			return;
		}
	} else {
		progress.value = Math.max(0, progress.value - elapsed / DRAIN_MS);
		if (progress.value <= 0) return;
	}
	frame = requestAnimationFrame(tick);
};

const run = () => {
	if (frame) return;
	lastTime = null;
	frame = requestAnimationFrame(tick);
};

const press = () => {
	if (props.disabled || done || holding.value) return;
	holding.value = true;
	run();
};

const release = () => {
	if (!holding.value) return;
	holding.value = false;
	run();
};

const handlePointerDown = (event) => {
	if (event.button === 0) press();
};

// Enter and Space hold the button like a finger does; Escape backs out.
const isHoldKey = (event) => event.key === 'Enter' || event.key === ' ';

const handleKeydown = (event) => {
	if (event.key === 'Escape') {
		event.preventDefault();
		event.stopPropagation();
		emit('cancel');
		return;
	}
	if (!isHoldKey(event)) return;
	event.preventDefault();
	if (!event.repeat) press();
};

const handleKeyup = (event) => {
	if (isHoldKey(event)) release();
};

onBeforeUnmount(() => {
	if (frame) cancelAnimationFrame(frame);
	clearTimeout(confirmTimer);
});
</script>

<style scoped>
.hold-confirm {
	position: relative;
	isolation: isolate;
	overflow: hidden;
	user-select: none;
	-webkit-user-select: none;
	-webkit-touch-callout: none;
}

/* A slightly deeper red than the hover tint, growing from the left edge while the button is held. */
.hold-confirm-fill {
	position: absolute;
	inset: 0;
	z-index: -1;
	background: color-mix(in srgb, var(--noc-red, #d32f2f) 14%, transparent);
	transform-origin: left center;
	pointer-events: none;
}
</style>
